// Adapted from https://github.com/getsentry/sentry-rrweb/blob/master/src/index.ts
// to support lazy loading & full set of rrweb options

import * as Sentry from '@sentry/browser';
import { DsnComponents, Event } from '@sentry/types';
import type { EventType, record } from 'rrweb';

type RRWebEvent = {
  type: EventType;
  data: {};
  timestamp: number;
  delay?: number;
};

type RRWebOptions = Parameters<typeof record>[0];

export default class SentryRRWeb {
  public readonly name: string = SentryRRWeb.id;
  public static id: string = 'SentryRRWeb';

  public events: Array<RRWebEvent> = [];

  private readonly recordOptions: RRWebOptions;

  public constructor({
    checkoutEveryNms = 5 * 60 * 1000,
    maskAllInputs = true,
    ...recordOptions
  }: RRWebOptions = {}) {
    // default checkout time of 5 minutes
    this.recordOptions = {
      checkoutEveryNms,
      maskAllInputs,
      ...recordOptions,
    };
    this.events = [];

    // Don't load client dependency rrweb if server rendering
    if (typeof window !== 'undefined') {
      import('rrweb').then((rrweb) => {
        rrweb.record({
          ...this.recordOptions,
          emit: (event: RRWebEvent, isCheckout?: boolean) => {
            if (isCheckout) {
              this.events = [event];
            } else {
              this.events.push(event);
            }
          },
        });
      });
    }
  }

  public setupOnce() {
    Sentry.addEventProcessor((event: Event | undefined) =>
      event ? this.processEvent(event) ?? null : null,
    );
  }

  public attachmentUrlFromDsn(dsn: DsnComponents, eventId: string) {
    const { host, path, projectId, port, protocol, publicKey } = dsn;
    return `${protocol}://${host}${port !== '' ? `:${port}` : ''}${
      path !== '' ? `/${path}` : ''
    }/api/${projectId}/events/${eventId}/attachments/?sentry_key=${publicKey}&sentry_version=7&sentry_client=rrweb`;
  }

  public processEvent(event: Event) {
    Sentry.getCurrentScope().addAttachment({
      filename: 'rrweb.json',
      contentType: 'application/json',
      data: JSON.stringify({ events: this.events }),
    });
    return event;
  }
}
